/**
 * ConfirmNoActivityButton - a FIRST statement blocked because it starts after the accounting start date.
 * The user confirms the account simply had no activity in between; the statement's opening balance becomes
 * the account's opening balance and the SAME upload resumes.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CalendarCheck2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { useBackgroundJobs } from '../../contexts/BackgroundJobsContext';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const fmtAmt = (v) => (v == null ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export const ConfirmNoActivityButton = ({ statement, onResumed }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { addJob } = useBackgroundJobs();
  const block = statement?.continuity_block || {};
  const jobId = statement?.job_id || statement?.id;
  const symbol = statement?.backlog_symbol || '';

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/bank-feeds/upload-jobs/${jobId}/confirm-no-activity`);
      toast.success(data.message || 'The upload is continuing.');
      addJob({ jobId, filename: statement?.filename, status: 'resuming', progress: 20 });
      setOpen(false);
      onResumed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not confirm - please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="px-1.5 py-0.5 rounded border border-emerald-600/40 text-emerald-700 hover:bg-emerald-50 font-medium inline-flex items-center gap-1"
        data-testid={`stmt-confirm-no-activity-${statement.id}`}
      >
        <CalendarCheck2 className="w-3 h-3" /> Confirm no activity
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg w-[94vw]" data-testid="confirm-no-activity-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarCheck2 className="w-5 h-5 text-emerald-700" /> No activity before the first transaction?
            </DialogTitle>
            <DialogDescription className="truncate">{statement?.filename}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              This statement&apos;s first transaction is dated <strong>{fmtDate(block.statement_start)}</strong>, after your
              accounting start date of <strong>{fmtDate(block.accounting_start_date)}</strong>.
            </p>
            <div className="rounded-lg border bg-slate-50 p-3 space-y-1" data-testid="confirm-no-activity-summary">
              <div className="flex justify-between"><span>Balance on {fmtDate(block.accounting_start_date)}</span><strong>{symbol}{fmtAmt(block.statement_opening)}</strong></div>
              <div className="flex justify-between"><span>No transactions</span><span>{fmtDate(block.accounting_start_date)} → {fmtDate(block.statement_start)}</span></div>
            </div>
            <p className="text-slate-600">
              By confirming, the statement&apos;s opening balance of <strong>{symbol}{fmtAmt(block.statement_opening)}</strong> is recorded as
              this account&apos;s opening balance on {fmtDate(block.accounting_start_date)} and the upload continues - no re-upload needed.
              If the account did have transactions in that window, close this and upload the statement that covers them first.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="confirm-no-activity-cancel">Cancel</Button>
            <Button onClick={confirm} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="confirm-no-activity-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Confirm &amp; continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
